import { computeDonutSlices } from './donut-chart.js';

/**
 * A labelled donut chart rendered as inline SVG with the amount repeated in
 * the centre. Slices respond to hover; the caller wires the active key to a
 * legend or table via onHover so they can highlight together.
 * @param {Object} props
 * @param {Record<string, number>} props.values key -> value; positive values get a slice
 * @param {import('react').ReactNode} props.amount amount shown in the centre
 * @param {string} [props.label] small label above the centre amount
 * @param {string} [props.ariaLabel] accessible name for the chart
 * @param {string | null} [props.activeKey] key of the slice to emphasise
 * @param {(key: string | null) => void} [props.onHover] slice hover / leave callback
 * @returns {JSX.Element}
 */
export function DonutChart({
	values,
	amount,
	label = '',
	ariaLabel = '',
	activeKey = null,
	onHover = () => {},
}) {
	const { entries } = computeDonutSlices(values);

	const handleOver = (event) => {
		const slice = /** @type {SVGPathElement | null} */ (event.target).closest('path');
		if (slice) { onHover(slice.dataset.key ?? null); }
	};

	return (
		<div className="donut-wrap">
			<svg
				className={`donut${activeKey ? ' is-hovering' : ''}`}
				viewBox="0 0 200 200"
				role="img"
				aria-label={ariaLabel}
				onMouseOver={handleOver}
				onMouseLeave={() => onHover(null)}
			>
				{entries.map((entry) => (
					<path
						key={entry.key}
						data-key={entry.key}
						className={`donut-slice-${entry.colorIndex}${activeKey === entry.key ? ' is-active' : ''}`}
						d={entry.d}
					/>
				))}
			</svg>
			<div className="donut-center">
				{label ? <span className="donut-center-label">{label}</span> : null}
				<span className="donut-center-value">{amount}</span>
			</div>
		</div>
	);
}
